/* eslint-disable no-control-regex */
import React, { useEffect, useState } from "react";
import _ from "lodash/fp";
import { Dialog, GridGenerator, Loading } from "@actinc/dls/components";
import {
  Button,
  DialogActions,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useForm, useFormState } from "react-hook-form";
import { useClient, useMutation } from "urql";

import {
  ControlledFormCheckbox,
  ControlledFormInput,
  ControlledFormSwitch,
} from "../../../components";

const AdminUserDialog = ({ closeDialog, programs, userID }) => {
  const client = useClient();
  const [, mutateUser] = useMutation(
    userID ? updateUserMutation : createUserMutation,
  );

  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState({ ...defaultValues });
  const { control, handleSubmit, reset, watch, setValue } = useForm({
    defaultValues: { ...defaultValues },
    mode: "all",
    reValidateMode: "onBlur",
  });
  const { dirtyFields, isDirty, isSubmitting, isValid } = useFormState({
    control,
  });

  const accountActive = watch("accountActive");
  const azureId = watch("azureId");

  useEffect(() => {
    if (accountActive === false && dirtyFields.accountActive) {
      setValue("superAdmin", false);
      programs.forEach((program) => {
        setValue(`programAccess.${program.value}.admin`, false);
        reviewTypeOptions.forEach((role) => {
          setValue(
            `programAccess.${program.value}.reviewTypes.${role.abbrev}`,
            false,
          );
        });
      });
    }
  }, [accountActive, dirtyFields.accountActive, programs, setValue]);

  useEffect(() => {
    if (!userID) return;

    async function getUser() {
      setLoading(true);
      const response = await client
        .query(getUserQuery, { id: userID })
        .toPromise();
      const data = response.data?.userByID;

      if (data) {
        setUser(data);
        reset(convertToDisplayFormat(programs)(data));
      }

      setLoading(false);
    }

    getUser();
  }, [client, programs, reset, userID]);

  const onSubmit = async (formData) => {
    const data = convertToStorageFormat(formData);
    await mutateUser(userID ? { data, id: userID } : { data }).then(() =>
      closeDialog(),
    );
  };

  const dialogProps = {
    draggable: false,
    fullWidth: true,
    maxWidth: "xl",
    onClose: closeDialog,
    open: true,
    scroll: "paper",
    title: userID ? `Edit User` : `Create User`,
  };

  return (
    <Dialog {...dialogProps}>
      <DialogContent
        dividers={true}
        sx={{
          paddingTop: 2,
        }}
      >
        {loading && (
          <Loading
            circularProgressProps={{
              size: 24,
              thickness: 4.5,
            }}
            title="Loading..."
          />
        )}
        {!loading && (!userID || user) && (
          <form id="userAdminForm" onSubmit={handleSubmit(onSubmit)}>
            <GridGenerator
              groups={[
                [
                  {
                    children: (
                      <ControlledFormInput
                        control={control}
                        fullWidth={true}
                        id="user-firstName"
                        label="First Name"
                        name="firstName"
                        rules={{
                          required: true,
                        }}
                      />
                    ),
                    key: `user-firstName`,
                  },
                  {
                    children: (
                      <ControlledFormInput
                        control={control}
                        fullWidth={true}
                        id="user-lastName"
                        label="Last Name"
                        name="lastName"
                        rules={{
                          required: true,
                        }}
                      />
                    ),
                    key: "user-lastName",
                  },
                ],
                [
                  {
                    children: (
                      <ControlledFormInput
                        control={control}
                        fullWidth={true}
                        id="user-email"
                        label="Email"
                        name="email"
                        rules={{
                          pattern: {
                            message:
                              "This field must contain a valid email address",
                            value:
                              /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/i,
                          },
                          required: true,
                        }}
                      />
                    ),
                    key: "user-email",
                  },
                  {
                    children: (
                      <ControlledFormInput
                        control={control}
                        disabled
                        fullWidth={true}
                        id="user-azureId"
                        label="Azure AD ID"
                        name="azureId"
                      />
                    ),
                    key: "user-azureId",
                  },
                ],
                [
                  {
                    children: (
                      <ControlledFormSwitch
                        control={control}
                        disabled={userID && (!azureId || azureId.trim() === "")}
                        id={`user-accountActive`}
                        label="Account Active"
                        name={`accountActive`}
                        value={true}
                      />
                    ),
                    key: "user-accountActive",
                  },
                  {
                    children: (
                      <ControlledFormCheckbox
                        checkboxProps={{ size: "small" }}
                        control={control}
                        id={`user-superAdmin`}
                        label="Super Admin"
                        name={`superAdmin`}
                        value={true}
                      />
                    ),
                    key: "user-superAdmin",
                  },
                ],
              ]}
            />
            <TableContainer sx={{ maxHeight: "400px" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Program</TableCell>
                    {reviewTypeOptions.map((role) => (
                      <TableCell
                        align="center"
                        key={`user-reviewType-${role.abbrev}`}
                        sx={{ maxWidth: "80px", overflowWrap: "break-word" }}
                      >
                        {role.value}
                      </TableCell>
                    ))}
                    <TableCell align="center">Admin</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!!programs.length &&
                    programs.map((program) => (
                      <TableRow key={`user-program-${program.value}`}>
                        <TableCell>{program.label}</TableCell>
                        {reviewTypeOptions.map((role) => (
                          <TableCell
                            align="center"
                            key={`user-program-${program.value}-reviewType-${role.abbrev}`}
                          >
                            <ControlledFormCheckbox
                              checkboxProps={{ size: "small" }}
                              control={control}
                              id={`user-programAccess.${program.value}.reviewTypes.${role.abbrev}`}
                              key={`user-programAccess-${program.value}.reviewTypes.${role.abbrev}`}
                              name={`programAccess.${program.value}.reviewTypes.${role.abbrev}`}
                              value={true}
                            />
                          </TableCell>
                        ))}
                        <TableCell align="center">
                          <ControlledFormCheckbox
                            checkboxProps={{
                              size: "small",
                            }}
                            control={control}
                            id={`user-programAccess.${program.value}.admin`}
                            key={`user-programAccess-${program.value}.admin`}
                            name={`programAccess.${program.value}.admin`}
                            value={true}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </form>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog} variant="text">
          Cancel
        </Button>
        <Button
          disabled={(!userID && !isDirty) || !isValid || isSubmitting}
          form="userAdminForm"
          type="submit"
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const userFields = `
  id
  accountActive
  azureId
  email
  firstName
  lastName
  programAccess {
    permissions
    program
    reviewTypes
  }
  superAdmin
`;
const createUserMutation = `
  mutation CreateUser($data: UserInput!) {
    createUser(data: $data) { ${userFields} }
  }`;
const getUserQuery = `
  query GetUser($id: ID!) {
    userByID(id: $id) { ${userFields} }
  }`;
const updateUserMutation = `
  mutation UpdateUser($id: ID!, $data: UserInput!) {
    updateUser(id: $id, data: $data) { ${userFields} }
  }`;

const convertToDisplayFormat =
  (programs) =>
  ({
    accountActive,
    azureId,
    email,
    firstName,
    lastName,
    programAccess,
    superAdmin,
  }) => ({
    accountActive: accountActive ?? true,
    azureId,
    email,
    firstName,
    lastName,
    programAccess: programs.length
      ? programs.reduce((acc1, program) => {
          const access = programAccess.find((a) => a.program === program.value);
          return {
            ...acc1,
            [program.value]: {
              admin: !!access?.permissions.includes("ProgramAdmin"),
              reviewTypes: reviewTypeOptions.reduce((acc2, reviewType) => {
                return {
                  ...acc2,
                  [reviewType.abbrev]:
                    !!access?.reviewTypes?.includes(reviewType.value) || false,
                };
              }, {}),
            },
          };
        }, {})
      : {},
    superAdmin,
  });
const convertToStorageFormat = ({
  accountActive,
  azureId,
  email,
  firstName,
  lastName,
  programAccess,
  superAdmin,
}) => ({
  accountActive: !!accountActive,
  azureId: azureId || null,
  email,
  firstName: firstName || null,
  lastName: lastName || null,
  programAccess: Object.keys(programAccess)
    .reduce((acc1, programID) => {
      const access = programAccess[programID];
      return [
        ...acc1,
        {
          permissions: access.admin ? ["ProgramAccess", "ProgramAdmin"] : [],
          program: programID,
          reviewTypes: Object.keys(access.reviewTypes).reduce(
            (acc2, abbrev) => {
              if (access.reviewTypes[abbrev]) {
                const role = reviewTypeOptions.find((r) => r.abbrev === abbrev);
                return [...acc2, role.value];
              } else {
                return [...acc2];
              }
            },
            [],
          ),
        },
      ];
    }, [])
    .filter((access) => access.reviewTypes.length || access.permissions.length)
    .map(({ permissions = [], ...access }) => {
      return {
        ...access,
        permissions: _.uniq(["ProgramAccess", ...permissions]),
      };
    }),
  superAdmin: !!superAdmin,
});

const defaultValues = {
  accountActive: true,
  azureId: "",
  email: "",
  firstName: "",
  lastName: "",
  programAccess: [],
  superAdmin: false,
};
const reviewTypeOptions = [
  { abbrev: "AS", value: "Accessibility Specialist" },
  { abbrev: "CS", value: "Content Specialist" },
  { abbrev: "ED", value: "Editor" },
  { abbrev: "ER", value: "External Reviewer" },
  { abbrev: "GD", value: "Graphic Designer" },
  { abbrev: "IW", value: "Item Writer" },
];

export {
  AdminUserDialog,
  convertToDisplayFormat,
  convertToStorageFormat,
  createUserMutation,
  getUserQuery,
  reviewTypeOptions,
  updateUserMutation,
};
